import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface NewsItem {
  _id: string;
  title: string;
  excerpt: string;
  content: string;
  images: Array<{
    filename: string;
    path: string;
    position: string;
  }>;
  imagePosition: string;
  publishedDate: string;
  category: string;
  isArchived: boolean;
}

const Home: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [settings, setSettings] = useState<any>(null);

  useEffect(() => {
    fetchNews();
    fetchSettings();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/news?limit=3`);
      if (response.data.success) {
        setNews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/homepage`);
      if (response.data.success) {
        setSettings(response.data.data);
        
        // Apply theme colors and font
        if (response.data.data.theme) {
          const theme = response.data.data.theme;
          document.documentElement.style.setProperty('--primary-color', theme.primaryColor);
          document.documentElement.style.setProperty('--secondary-color', theme.secondaryColor);
          document.documentElement.style.setProperty('--accent-color', theme.accentColor);
          document.documentElement.style.setProperty('--bg-color', theme.backgroundColor);
          document.documentElement.style.setProperty('--text-color', theme.textColor);
          document.body.style.fontFamily = theme.fontFamily;
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleNewsClick = (newsItem: NewsItem) => {
    setSelectedNews(newsItem);
    setShowNewsModal(true);
  };

  const handleCloseModal = () => {
    setShowNewsModal(false);
    setSelectedNews(null);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-primary to-primary-dark text-white py-20 bg-cover bg-center"
        style={{
          backgroundImage: settings?.hero?.backgroundImage 
            ? `linear-gradient(rgba(37, 99, 235, 0.8), rgba(30, 64, 175, 0.8)), url(http://localhost:5000${settings.hero.backgroundImage})`
            : undefined
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-12">
            {/* Logo on the left */}
            {settings?.logo && (
              <div className="hidden md:block flex-shrink-0">
                <img 
                  src={`http://localhost:5000${settings.logo}`} 
                  alt="Leftley Sports Club Logo" 
                  className="h-96 w-auto object-contain"
                />
              </div>
            )}
            
            {/* Text content */}
            <div className="text-center md:text-left flex-1">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                {settings?.hero?.title || 'Welcome to Leftley Sports Club'}
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-100">
                {settings?.hero?.subtitle || 'Building a stronger community through sports and activities since 2016'}
              </p>
              <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-4">
                <Link to="/membership" className="btn-secondary text-lg px-8 py-4">
                  {settings?.hero?.buttonText || 'Become a Member'}
                </Link>
                <Link to="/community-hub" className="bg-white text-primary hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition duration-300 text-lg">
                  {settings?.hero?.secondaryButtonText || 'Hire the Hub'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="p-6">
              <div className="text-5xl font-bold text-primary mb-2">{settings?.stats?.members || '500+'}</div>
              <div className="text-gray-600 text-lg">Active Members</div>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold text-primary mb-2">{settings?.stats?.activities || '5+'}</div>
              <div className="text-gray-600 text-lg">Sports & Activities</div>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold text-primary mb-2">{settings?.stats?.yearEstablished || 'Since 2016'}</div>
              <div className="text-gray-600 text-lg">Serving Community</div>
            </div>
            <div className="p-6">
              <div className="text-5xl font-bold text-primary mb-2">{settings?.stats?.hubCapacity || '80'}</div>
              <div className="text-gray-600 text-lg">Hub Capacity</div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership & Hub Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Become a Member */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition duration-300">
              <div className="p-8">
                <div className="text-4xl mb-4">👥</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{settings?.sections?.membershipHeading || 'Become a Member'}</h3>
                <p className="text-gray-600 mb-6">
                  Join our community of 500+ members and get access to all our sports and activities. 
                  Membership is required to participate in our activities.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Access to all activities</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Community hub benefits</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Unique member ID</span>
                  </li>
                </ul>
                <Link to="/membership" className="btn-primary w-full text-center block">
                  Register Now
                </Link>
              </div>
            </div>

            {/* Hire the Hub */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition duration-300">
              <div className="p-8">
                <div className="text-4xl mb-4">🏛️</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{settings?.sections?.hubHeading || 'Hire the Community Hub'}</h3>
                <p className="text-gray-600 mb-6">
                  Our community hub is perfect for events, meetings, and celebrations. 
                  Modern facilities accommodating up to 80 guests.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Capacity for 80 guests</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Modern facilities</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Easy online booking</span>
                  </li>
                </ul>
                <Link to="/community-hub" className="btn-primary w-full text-center block">
                  Check Availability
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">{settings?.sections?.activitiesHeading || 'Our Activities'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {/* Football */}
            <Link to="/activities/football" className="card text-center hover:transform hover:scale-105 transition duration-300">
              <div className="text-5xl mb-4">⚽</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Football</h3>
              <p className="text-gray-600 text-sm">Weekly sessions</p>
            </Link>

            {/* Walk & Talk */}
            <Link to="/activities/walk-and-talk" className="card text-center hover:transform hover:scale-105 transition duration-300">
              <div className="text-5xl mb-4">🚶</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Walk & Talk</h3>
              <p className="text-gray-600 text-sm">Stay active together</p>
            </Link>

            {/* Coffee Mornings */}
            <Link to="/activities/womens-coffee-mornings" className="card text-center hover:transform hover:scale-105 transition duration-300">
              <div className="text-5xl mb-4">☕</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Coffee Mornings</h3>
              <p className="text-gray-600 text-sm">Women's social events</p>
            </Link>

            {/* Aerobics */}
            <Link to="/activities/womens-aerobics" className="card text-center hover:transform hover:scale-105 transition duration-300">
              <div className="text-5xl mb-4">🤸</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Women's Aerobics</h3>
              <p className="text-gray-600 text-sm">Fitness sessions</p>
            </Link>

            {/* Cycling */}
            <Link to="/activities/cycling" className="card text-center hover:transform hover:scale-105 transition duration-300">
              <div className="text-5xl mb-4">🚴</div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Cycling</h3>
              <p className="text-gray-600 text-sm">Group rides</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">{settings?.sections?.newsHeading || '📰 Latest News'}</h2>
          {loading ? (
            <div className="text-center">Loading news...</div>
          ) : news.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {news.map((item) => (
                <div 
                  key={item._id} 
                  onClick={() => handleNewsClick(item)}
                  className="card hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                >
                  {item.images && item.images.length > 0 && item.imagePosition === 'top' && (
                    <img 
                      src={`http://localhost:5000${item.images[0].path}`}
                      alt={item.title} 
                      className="w-full h-48 object-cover rounded-t-lg mb-4"
                    />
                  )}
                  <div className="p-4">
                    <div className="inline-block bg-primary text-white text-xs px-3 py-1 rounded-full mb-3">
                      {item.category}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                    {item.excerpt && (
                      <p className="text-gray-600 text-sm mb-4">{item.excerpt}</p>
                    )}
                    {!item.excerpt && item.content && (
                      <p className="text-gray-600 text-sm mb-4">
                        {item.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                      </p>
                    )}
                    <p className="text-xs text-gray-500 flex items-center">
                      📅 {new Date(item.publishedDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <button className="mt-4 text-primary hover:text-primary-dark font-semibold text-sm flex items-center">
                      Read Full Article →
                    </button>
                  </div>
                  {item.images && item.images.length > 0 && item.imagePosition === 'bottom' && (
                    <img 
                      src={`http://localhost:5000${item.images[0].path}`}
                      alt={item.title} 
                      className="w-full h-48 object-cover rounded-b-lg mt-4"
                    />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-600 py-8">
              <p className="text-lg">No news available at the moment. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* News Article Modal */}
      {showNewsModal && selectedNews && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseModal}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
              <div className="flex-1">
                <div className="inline-block bg-primary text-white text-xs px-3 py-1 rounded-full mb-2">
                  {selectedNews.category}
                </div>
                <h2 className="text-3xl font-bold text-gray-800">{selectedNews.title}</h2>
                <p className="text-sm text-gray-500 mt-2">
                  📅 {new Date(selectedNews.publishedDate).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="ml-4 text-gray-500 hover:text-gray-700 text-3xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Images at Top */}
              {selectedNews.images && selectedNews.images.length > 0 && selectedNews.imagePosition === 'top' && (
                <div className="mb-6">
                  {selectedNews.images.length === 1 ? (
                    <img 
                      src={`http://localhost:5000${selectedNews.images[0].path}`}
                      alt={selectedNews.title}
                      className="w-full h-auto max-h-96 object-cover rounded-lg shadow-md"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedNews.images.map((img, index) => (
                        <img 
                          key={index}
                          src={`http://localhost:5000${img.path}`}
                          alt={`${selectedNews.title} - Image ${index + 1}`}
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Article Content */}
              <div 
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedNews.content }}
              />

              {/* Images at Bottom */}
              {selectedNews.images && selectedNews.images.length > 0 && selectedNews.imagePosition === 'bottom' && (
                <div className="mt-6">
                  {selectedNews.images.length === 1 ? (
                    <img 
                      src={`http://localhost:5000${selectedNews.images[0].path}`}
                      alt={selectedNews.title}
                      className="w-full h-auto max-h-96 object-cover rounded-lg shadow-md"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {selectedNews.images.map((img, index) => (
                        <img 
                          key={index}
                          src={`http://localhost:5000${img.path}`}
                          alt={`${selectedNews.title} - Image ${index + 1}`}
                          className="w-full h-64 object-cover rounded-lg shadow-md"
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
